import React, { useState, useEffect } from 'react';
import api from '../../services/api'; // Use centralized api
import { useAuth } from '../../contexts/AuthContext';
import './UploadPdf.css';

function UploadPdf({ onUploadSuccess }) {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    documentCategory: '',
    typeOfDocument: 'general',
    aiTutorRole: 'general_tutor'
  });
  const [file, setFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  // New Category State
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  // Default categories definition
  const defaultCategories = [
    { id: 'general', name: 'General', color: '#6B7280' },
    { id: 'work', name: 'Work', color: '#3B82F6' },
    { id: 'personal', name: 'Personal', color: '#10B981' },
    { id: 'research', name: 'Research', color: '#8B5CF6' },
    { id: 'education', name: 'Education', color: '#EF4444' }
  ];

  // Fetch user's categories
  useEffect(() => {
    const fetchCategories = async () => {
      if (!currentUser) return;

      try {
        // api client handles token and base URL
        const response = await api.get('/upload-pdf/categories');

        if (response.data.success) {
          const userCategories = response.data.categories || [];

          // Merge defaults with user categories
          const mergedCategories = [...defaultCategories];
          userCategories.forEach(cat => {
            if (!mergedCategories.some(c => c.id === cat.id)) {
              mergedCategories.push(cat);
            }
          });

          setCategories(mergedCategories);

          // Set default category
          if (mergedCategories.length > 0) {
            setFormData(prev => ({
              ...prev,
              documentCategory: JSON.stringify(mergedCategories[0])
            }));
          }
        }
      } catch (error) {
        console.error('❌ Failed to fetch categories:', error);
        setCategories(defaultCategories);
        setFormData(prev => ({
          ...prev,
          documentCategory: JSON.stringify(defaultCategories[0])
        }));
      }
    };

    fetchCategories();
  }, [currentUser]);

  // ... rest of the UploadPdf component remains the same
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        setError('Please select a PDF file');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setFile(selectedFile);
      setError('');

      // Auto-fill name if empty
      if (!formData.name) {
        const fileName = selectedFile.name.replace('.pdf', '');
        setFormData(prev => ({
          ...prev,
          name: fileName
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('Please select a PDF file');
      return;
    }

    if (!formData.name.trim()) {
      setError('Please enter a document name');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setUploadProgress(0);

      const uploadData = new FormData();

      // Append file
      uploadData.append('pdf', file);

      // Append form data
      Object.keys(formData).forEach(key => {
        uploadData.append(key, formData[key]);
      });

      const response = await api.post('/upload-pdf', uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      if (response.data.success) {
        console.log('✅ PDF uploaded successfully:', response.data);
        alert('PDF uploaded and processed successfully!');

        // Reset form
        setFormData({
          name: '',
          description: '',
          documentCategory: categories.length > 0 ? JSON.stringify(categories[0]) : '',
          typeOfDocument: 'general',
          aiTutorRole: 'general_tutor'
        });
        setFile(null);
        setUploadProgress(0);

        // Notify parent component
        onUploadSuccess();
      } else {
        throw new Error(response.data.error || 'Upload failed');
      }

    } catch (error) {
      console.error('❌ Upload failed:', error);
      setError(error.response?.data?.error || error.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      setCreatingCategory(true);
      // api client handles token and base URL
      const response = await api.post('/upload-pdf/categories', { name: newCategoryName });

      if (response.data.success) {
        const newCat = response.data.category;
        setCategories(prev => [...prev, newCat]);
        setFormData(prev => ({
          ...prev,
          documentCategory: JSON.stringify(newCat)
        }));
        setNewCategoryName('');
        setIsCreatingCategory(false);
      }
    } catch (err) {
      console.error("Failed to create category:", err);
      const errorMsg = err.response?.data?.error || "Failed to create category";
      alert(errorMsg);
    } finally {
      setCreatingCategory(false);
    }
  };

  return (
    <div className="upload-pdf">
      <div className="upload-card">
        <h2>Upload New PDF</h2>
        <p className="upload-subtitle">Add a new document to your library</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="upload-form">
          {/* File Upload */}
          <div className="form-group">
            <label className="form-label">PDF File *</label>
            <div className="file-upload-area">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="file-input"
                id="pdf-upload"
              />
              <label htmlFor="pdf-upload" className="file-upload-label">
                {file ? (
                  <div className="file-selected">
                    <span className="file-icon">📄</span>
                    <div className="file-info">
                      <div className="file-name">{file.name}</div>
                      <div className="file-size">{(file.size / (1024 * 1024)).toFixed(2)} MB</div>
                    </div>
                  </div>
                ) : (
                  <div className="file-placeholder">
                    <span className="upload-icon">📤</span>
                    <div className="upload-text">
                      <div className="upload-title">Click to upload PDF</div>
                      <div className="upload-subtext">Max file size: 10MB</div>
                    </div>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Document Name */}
          <div className="form-group">
            <label className="form-label">Document Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter document title"
              required
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder="Brief description of the document"
              rows="3"
            />
          </div>

          {/* Category */}
          <div className="form-group">
            <div className="category-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label className="form-label" style={{ marginBottom: 0 }}>Category</label>
              <button
                type="button"
                className="text-button"
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2563EB',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  padding: 0
                }}
                onClick={() => setIsCreatingCategory(!isCreatingCategory)}
              >
                {isCreatingCategory ? 'Cancel' : '+ New Category'}
              </button>
            </div>

            {isCreatingCategory ? (
              <div className="new-category-input" style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Enter category name"
                  className="form-input"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={handleCreateCategory}
                  disabled={!newCategoryName.trim() || creatingCategory}
                  className="action-button"
                  style={{
                    backgroundColor: '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0 12px',
                    cursor: 'pointer',
                    opacity: (!newCategoryName.trim() || creatingCategory) ? 0.7 : 1
                  }}
                >
                  {creatingCategory ? '...' : 'Add'}
                </button>
              </div>
            ) : (
              <select
                name="documentCategory"
                value={formData.documentCategory}
                onChange={handleInputChange}
                className="form-select"
              >
                {categories.map(category => (
                  <option key={category.id} value={JSON.stringify(category)}>
                    {category.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Document Type */}
          <div className="form-group">
            <label className="form-label">Document Type</label>
            <select
              name="typeOfDocument"
              value={formData.typeOfDocument}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="general">General</option>
              <option value="academic">Academic</option>
              <option value="research">Research</option>
              <option value="business">Business</option>
              <option value="technical">Technical</option>
            </select>
          </div>

          {/* AI Tutor Role */}
          <div className="form-group">
            <label className="form-label">AI Tutor Role</label>
            <select
              name="aiTutorRole"
              value={formData.aiTutorRole}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="general_tutor">General Tutor</option>
              <option value="research_assistant">Research Assistant</option>
              <option value="technical_expert">Technical Expert</option>
              <option value="business_analyst">Business Analyst</option>
              <option value="academic_tutor">Academic Tutor</option>
            </select>
          </div>

          {/* Upload Progress */}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <div className="progress-text">Uploading: {uploadProgress}%</div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !file}
            className="submit-button"
          >
            {loading ? 'Uploading...' : 'Upload PDF'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default UploadPdf;